import { c as classNames } from './index-8887fc24.js';
import { r as requiredArgs, t as toDate, a as toInteger, b as addMonths, s as startOfMonth, c as subYears, d as startOfYear, f as addYears, C as ChevronLeftIcon, Y as YearPicker } from './index-23844e78.js';
import * as React from 'react';
import './tslib.es6-18cb2814.js';
import { A as ArrowDownIcon } from './ArrowDownIcon-fbdb97a6.js';
import { C as ChevronRightIcon } from './ChevronRightIcon-b72559d2.js';
import './countries-34fea817.js';
import { B as Button } from './index-b7114926.js';
import { h as FocusTrap } from './index-21f083b4.js';
import { P as Popover } from './index-b7606a36.js';

/**
 * @name endOfYear
 * @category Year Helpers
 * @summary Return the end of a year for the given date.
 *
 * @description
 * Return the end of a year for the given date.
 * The result will be in the local timezone.
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the end of a year
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The end of a year for 2 September 2014 11:55:00:
 * const result = endOfYear(new Date(2014, 8, 2, 11, 55, 00))
 * //=> Wed Dec 31 2014 23:59:59.999
 */
function endOfYear(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getFullYear();
  date.setFullYear(year + 1, 0, 0);
  date.setHours(23, 59, 59, 999);
  return date;
}

/**
 * @name getDaysInMonth
 * @category Month Helpers
 * @summary Get the number of days in a month of the given date.
 *
 * @description
 * Get the number of days in a month of the given date.
 *
 * @param {Date|Number} date - the given date
 * @returns {Number} the number of days in a month
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // How many days are in February 2000?
 * const result = getDaysInMonth(new Date(2000, 1))
 * //=> 29
 */
function getDaysInMonth(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getFullYear();
  var monthIndex = date.getMonth();
  var lastDayOfMonth = new Date(0);
  lastDayOfMonth.setFullYear(year, monthIndex + 1, 0);
  lastDayOfMonth.setHours(0, 0, 0, 0);
  return lastDayOfMonth.getDate();
}

/**
 * @name setMonth
 * @category Month Helpers
 * @summary Set the month to the given date.
 *
 * @description
 * Set the month to the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} month - the month of the new date
 * @returns {Date} the new date with the month set
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Set February to 1 September 2014:
 * const result = setMonth(new Date(2014, 8, 1), 1)
 * //=> Sat Feb 01 2014 00:00:00
 */
function setMonth(dirtyDate, dirtyMonth) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var month = toInteger(dirtyMonth);
  var year = date.getFullYear();
  var day = date.getDate();
  var dateWithDesiredMonth = new Date(0);
  dateWithDesiredMonth.setFullYear(year, month, 15);
  dateWithDesiredMonth.setHours(0, 0, 0, 0);
  var daysInMonth = getDaysInMonth(dateWithDesiredMonth);
  // Set the last day of the new month
  // if the original date was the last day of the longer month
  date.setMonth(month, Math.min(day, daysInMonth));
  return date;
}

var MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];
var SHORT_MONTH_NAMES = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];
var ALL_MONTHS = MONTH_NAMES.map(function (n, i) { return ({
    index: i,
    name: n,
    shortName: SHORT_MONTH_NAMES[i],
}); });
var createMonthsRange = function (startDate, months) {
    return Array.from(Array(months).keys()).map(function (d) { return addMonths(startDate, d); });
};

var styles = {"wrapper":"MonthPicker_wrapper_1fd02add","picker":"MonthPicker_picker_1fd02add","months":"MonthPicker_months_1fd02add","yearPickerButton":"MonthPicker_yearPickerButton_1fd02add","header":"MonthPicker_header_1fd02add","focus-visible":"MonthPicker_focus-visible_1fd02add","arrowDown":"MonthPicker_arrowDown_1fd02add","pickerControl":"MonthPicker_pickerControl_1fd02add","controls":"MonthPicker_controls_1fd02add","control":"MonthPicker_control_1fd02add","chevronLeft":"MonthPicker_chevronLeft_1fd02add","cell":"MonthPicker_cell_1fd02add"};

var MonthPicker = React.forwardRef(function MonthPicker(_a, ref) {
    var className = _a.className, selectedDate = _a.selectedDate, onMonthSelect = _a.onMonthSelect, onYearSelectProp = _a.onYearSelect, boundingRange = _a.boundingRange, _b = _a.hideYearPicker, hideYearPicker = _b === void 0 ? false : _b, _c = _a.isPopover, isPopover = _c === void 0 ? false : _c, onOpenYearPickerProp = _a.onOpenYearPicker, numYears = _a.numYears;
    var _d = React.useState(false), isYearPicker = _d[0], setIsYearPicker = _d[1];
    var yearPickerButton = React.useRef(null);
    var _e = React.useState(selectedDate || new Date()), currentVisibleDate = _e[0], setCurrentVisibleDate = _e[1];
    var _f = boundingRange || [], minDate = _f[0], maxDate = _f[1];
    var isInRange = React.useCallback(function (date) {
        return (minDate ? startOfMonth(date) >= startOfMonth(minDate) : true) &&
            (maxDate ? startOfMonth(date) <= startOfMonth(maxDate) : true);
    }, [maxDate, minDate]);
    var showPrevious = React.useMemo(function () { return isInRange(endOfYear(subYears(currentVisibleDate, 1))); }, [currentVisibleDate, isInRange]);
    var showNext = React.useMemo(function () { return isInRange(startOfYear(addYears(currentVisibleDate, 1))); }, [currentVisibleDate, isInRange]);
    var onOpenYearPicker = React.useCallback(function () {
        if (isPopover && onOpenYearPickerProp) {
            onOpenYearPickerProp();
        }
        else {
            setIsYearPicker(true);
        }
    }, [isPopover, onOpenYearPickerProp]);
    var onCloseYearPicker = React.useCallback(function () {
        setIsYearPicker(false);
    }, []);
    var onPreviousClick = React.useCallback(function () {
        var newYear = subYears(currentVisibleDate, 1);
        setCurrentVisibleDate(newYear);
    }, [currentVisibleDate]);
    var onNextClick = React.useCallback(function () {
        var newYear = addYears(currentVisibleDate, 1);
        setCurrentVisibleDate(newYear);
    }, [currentVisibleDate]);
    var onYearSelect = React.useCallback(function (date) {
        onYearSelectProp && onYearSelectProp(date);
        setCurrentVisibleDate(date);
        onCloseYearPicker();
    }, [onCloseYearPicker, onYearSelectProp]);
    return (React.createElement("div", { ref: ref, className: classNames(styles.wrapper, className), "data-testid": "month-picker" },
        React.createElement("div", { className: styles.picker },
            React.createElement("div", { className: styles.header },
                React.createElement(Button, { variant: "secondary", color: "interface", label: "Previous Year", "data-testid": "month-picker-previous-button", onClick: onPreviousClick, disabled: !showPrevious, size: "small" },
                    React.createElement(ChevronLeftIcon, null)),
                React.createElement("div", { className: styles.pickerControl, "data-testid": "month-picker-year-heading" }, !hideYearPicker ? (React.createElement(Popover, { isOpen: isYearPicker, onClose: onCloseYearPicker, placement: "bottom", "data-testid": "month-picker-year-popover", content: React.createElement(FocusTrap, null,
                        React.createElement(YearPicker, { selectedDate: selectedDate, onYearSelect: onYearSelect, boundingRange: boundingRange, numYears: numYears })) },
                    React.createElement(Button, { variant: "secondary", color: "interface", size: "small", className: styles.yearPickerButton, "data-testid": "month-picker-year-button", onClick: onOpenYearPicker, ref: yearPickerButton },
                        currentVisibleDate.getFullYear(),
                        React.createElement(ArrowDownIcon, { className: styles.arrowDown })))) : (currentVisibleDate.getFullYear())),
                React.createElement(Button, { variant: "secondary", color: "interface", size: "small", label: "Next Year", "data-testid": "month-picker-next-button", onClick: onNextClick, disabled: !showNext },
                    React.createElement(ChevronRightIcon, null))),
            React.createElement("div", { className: styles.months }, ALL_MONTHS.map(function (month, i) { return (React.createElement("button", { "data-testid": "month-picker-month", "data-active-month": selectedDate &&
                    currentVisibleDate.getFullYear() ===
                        selectedDate.getFullYear()
                    ? selectedDate.getMonth() === i
                    : false, "data-inrange": isInRange(setMonth(currentVisibleDate, i)), className: styles.cell, key: i, onClick: function () {
                    onMonthSelect &&
                        onMonthSelect(new Date(currentVisibleDate.getFullYear(), i));
                } }, month.shortName)); })))));
});

export { MonthPicker as M, MONTH_NAMES as a, createMonthsRange as c };
